import { useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Role } from "@chat-yai/shared";
import { Icon } from "./Icon";
import { useSession } from "@/contexts/SessionContext";
import { tenantsApi } from "@/api/endpoints/tenants";
import { authStorage } from "@/lib/auth-storage";
import { useToast } from "@/contexts/ToastContext";
import { translateError } from "@/i18n/errors";
import { ApiError } from "@/api/client";

interface NavGroup {
  group: string;
  items: NavItem[];
}
interface NavItem {
  id: string;
  label: string;
  icon:
    | "dashboard"
    | "team"
    | "user"
    | "history"
    | "settings"
    | "shield"
    | "bot"
    | "globe"
    | "plug"
    | "mail"
    | "bell"
    | "trend";
  to: string;
  visibleFor?: Role[];
  badge?: string | number;
}

const TENANT_NAV: NavGroup[] = [
  {
    group: "WORKSPACE",
    items: [
      { id: "dashboard", label: "Dashboard", icon: "dashboard", to: "/dashboard" },
      { id: "chatbots", label: "Chatbots", icon: "bot", to: "/chatbots" },
      {
        id: "conversations",
        label: "Conversations",
        icon: "mail",
        to: "/conversations",
      },
      {
        id: "sites",
        label: "Sites",
        icon: "globe",
        to: "/sites",
        visibleFor: ["owner", "admin", "viewer"],
      },
      { id: "members", label: "Members", icon: "team", to: "/members" },
      {
        id: "audit",
        label: "Audit log",
        icon: "history",
        to: "/audit-logs",
        visibleFor: ["owner", "admin"],
      },
    ],
  },
  {
    group: "INSIGHTS",
    items: [
      {
        id: "usage",
        label: "Usage",
        icon: "trend",
        to: "/usage",
        // Brief §5: Owner/Admin only in the Sidebar. Viewer can still hit
        // the route directly (read-only) and Support is route-gated out.
        visibleFor: ["owner", "admin"],
      },
      {
        id: "notifications",
        label: "Notifications",
        icon: "bell",
        to: "/notifications",
        visibleFor: ["owner", "admin"],
      },
    ],
  },
  {
    group: "INTEGRATIONS",
    items: [
      {
        id: "integrations-llm",
        label: "LLM keys",
        icon: "plug",
        to: "/integrations/llm",
        visibleFor: ["owner", "admin"],
      },
      {
        id: "integrations-twilio",
        label: "Twilio",
        icon: "plug",
        to: "/integrations/twilio",
        visibleFor: ["owner", "admin"],
      },
      {
        id: "phone-numbers",
        label: "Phone numbers",
        icon: "plug",
        to: "/phone-numbers",
        visibleFor: ["owner", "admin"],
      },
    ],
  },
  {
    group: "ACCOUNT",
    items: [{ id: "profile", label: "Profile", icon: "user", to: "/profile" }],
  },
];

const SUPER_NAV: NavGroup[] = [
  {
    group: "OVERVIEW",
    items: [{ id: "tenants", label: "Tenants", icon: "team", to: "/super/tenants" }],
  },
  {
    group: "PLATFORM",
    items: [{ id: "audit", label: "Audit log", icon: "history", to: "/super/audit-logs" }],
  },
];

interface SidebarProps {
  // Mobile drawer open state. Desktop layouts pass nothing and the sidebar
  // is always visible (controlled by CSS media queries).
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function TenantSidebar({ mobileOpen, onMobileClose }: SidebarProps = {}): JSX.Element {
  const { me } = useSession();
  const location = useLocation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const currentMembership = me?.memberships.find((m) => m.tenant_id === me.current_tenant_id);
  const role: Role = currentMembership?.role ?? "viewer";

  const switchMutation = useMutation({
    mutationFn: (tenantId: string) => tenantsApi.switch({ tenant_id: tenantId }),
    onSuccess: async (res) => {
      authStorage.set(res.access_token);
      await queryClient.invalidateQueries();
      setPopoverOpen(false);
      navigate({ to: "/dashboard" });
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  return (
    <aside
      className={"sidebar" + (mobileOpen ? " sidebar-open" : "")}
      data-testid="tenant-sidebar"
    >
      <div className="sidebar-head">
        <div className="brand">
          <div className="brand-mark">
            B<span className="dot">.</span>
          </div>
          <div className="brand-name">BestAI FrontDesk</div>
        </div>
        <button
          className="tenant-switch"
          type="button"
          onClick={() => setPopoverOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={popoverOpen}
          data-testid="tenant-switcher"
        >
          <div className="tenant-avatar">
            {(currentMembership?.name ?? "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="tenant-switch-body">
            <div className="tenant-name">{currentMembership?.name ?? "Select workspace"}</div>
            <div className="tenant-plan">
              {role.charAt(0).toUpperCase() + role.slice(1)} ·{" "}
              {currentMembership?.slug ?? "—"}
            </div>
          </div>
          <Icon name="chevron-down" size={14} />
        </button>
        {popoverOpen && me ? (
          <div className="tenant-popover" role="listbox">
            {me.memberships.map((m) => (
              <button
                key={m.tenant_id}
                onClick={() => switchMutation.mutate(m.tenant_id)}
                className={m.tenant_id === me.current_tenant_id ? "active" : ""}
                data-testid={`tenant-option-${m.slug}`}
              >
                <div className="tenant-avatar" style={{ width: 22, height: 22, fontSize: 10 }}>
                  {m.name.slice(0, 1).toUpperCase()}
                </div>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{m.name}</span>
                  <span style={{ color: "var(--text-3)", fontSize: 11 }}>{m.role}</span>
                </div>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {TENANT_NAV.map((group) => {
        const items = group.items.filter((it) => !it.visibleFor || it.visibleFor.includes(role));
        if (items.length === 0) return null;
        return (
          <div className="sidebar-section" key={group.group}>
            <div className="sidebar-section-label">{group.group}</div>
            {items.map((it) => {
              const active = location.pathname.startsWith(it.to);
              return (
                <Link
                  key={it.id}
                  to={it.to as "/dashboard"}
                  className={"nav-item " + (active ? "active" : "")}
                  data-testid={`nav-${it.id}`}
                  onClick={() => onMobileClose?.()}
                >
                  <Icon name={it.icon} />
                  <span className="label">{it.label}</span>
                  {it.badge ? <span className="badge">{it.badge}</span> : null}
                </Link>
              );
            })}
          </div>
        );
      })}

      <div className="sidebar-foot">
        <div className="help" style={{ padding: "0 8px" }}>
          Signed in as <span style={{ color: "var(--text-1)" }}>{me?.email}</span>
        </div>
      </div>
    </aside>
  );
}

export function SuperSidebar({ mobileOpen }: SidebarProps = {}): JSX.Element {
  const location = useLocation();
  const { me } = useSession();
  return (
    <aside
      className={"sidebar sidebar-super" + (mobileOpen ? " sidebar-open" : "")}
      data-testid="super-sidebar"
    >
      <div className="sidebar-head">
        <div className="brand">
          <div className="brand-mark">
            B<span className="dot" style={{ color: "oklch(0.6 0.18 25)" }}>.</span>
          </div>
          <div className="brand-name">
            BestAI FrontDesk<span className="brand-tag">SUPER</span>
          </div>
        </div>
        <div className="tenant-switch disabled" aria-disabled>
          <div
            className="tenant-avatar"
            style={{ background: "oklch(0.5 0.16 25)" }}
          >
            S
          </div>
          <div className="tenant-switch-body">
            <div className="tenant-name">Platform · super admin</div>
            <div className="tenant-plan">Staff · {me?.email}</div>
          </div>
        </div>
      </div>

      {SUPER_NAV.map((group) => (
        <div className="sidebar-section" key={group.group}>
          <div className="sidebar-section-label">{group.group}</div>
          {group.items.map((it) => {
            const active = location.pathname.startsWith(it.to);
            return (
              <Link
                key={it.id}
                to={it.to as "/super/tenants"}
                className={"nav-item " + (active ? "active" : "")}
                data-testid={`super-nav-${it.id}`}
              >
                <Icon name={it.icon} />
                <span className="label">{it.label}</span>
              </Link>
            );
          })}
        </div>
      ))}

      <div className="sidebar-foot">
        <Link to="/dashboard" className="nav-item">
          <Icon name="external" />
          <span className="label">Back to tenant</span>
        </Link>
      </div>
    </aside>
  );
}
